import React from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ChannelIcon from './ChannelIcon';
import { INTEREST_CATEGORIES } from '../../constants/categories';
import { COUNTRIES } from '../../constants/countries';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  data: any;
}

const CampaignPreviewModal: React.FC<Props> = ({ isOpen, onClose, data }) => {
  const { t } = useTranslation(['campaigns', 'common']);

  if (!isOpen || !data) return null;

  const Field: React.FC<{ label: string; value?: any; children?: React.ReactNode }> = ({ label, value, children }) => {
    if (!value && !children) return null;
    return (
      <div className="flex justify-between py-2 border-b border-gray-100">
        <span className="text-gray-600 text-sm">{label}</span>
        <span className="font-medium text-gray-900 text-sm text-right max-w-[60%]">{children || value}</span>
      </div>
    );
  };

  const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="mb-6">
      <h3 className="text-base font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">{title}</h3>
      <div className="space-y-1">{children}</div>
    </div>
  );

  const influencerAmount = data.rewardAmount ? Math.round(data.rewardAmount * 0.8) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900">{t('campaigns:registration.preview')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="w-5 h-5" /></button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">

          {/* Representative Images */}
          {data.representativeImages?.length > 0 && (
            <div className="mb-4 flex gap-2 overflow-x-auto">
              {data.representativeImages.map((img: any, i: number) => (
                <img key={i} src={img.fileUrl} alt="" className="w-24 h-24 rounded-lg object-cover flex-shrink-0" />
              ))}
            </div>
          )}

          {/* Section 1: Campaign Basic Info */}
          <Section title={`1. ${t('campaigns:registration.v2.sectionBasic')}`}>
            <Field label={t('campaigns:registration.v2.brandName')} value={data.brandName} />
            <Field label={t('campaigns:registration.v2.campaignName')} value={data.campaignName} />
            <Field label={t('campaigns:registration.v2.campaignCategory')}>
              {data.campaignCategory && t(`common:categories.${data.campaignCategory}`, data.campaignCategory)}
            </Field>
          </Section>

          {/* Section 2: Product/Service Info */}
          <Section title={`2. ${t('campaigns:registration.v2.sectionProduct')}`}>
            {data.productIntroduction && (
              <div className="py-2 border-b border-gray-100">
                <span className="text-gray-600 text-sm block mb-1">{t('campaigns:registration.v2.productIntroduction')}</span>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{data.productIntroduction}</p>
              </div>
            )}
            <Field label={t('campaigns:registration.v2.productName')} value={data.productName} />
            <Field label={t('campaigns:registration.v2.productValue')} value={data.providedValueDescription} />
            <Field label={t('campaigns:registration.v2.provisionMethod')} value={data.provisionMethod} />
            {data.additionalNotes && (
              <div className="py-2 border-b border-gray-100">
                <span className="text-gray-600 text-sm block mb-1">{t('campaigns:registration.v2.visitNotes')}</span>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{data.additionalNotes}</p>
              </div>
            )}
            <Field label={t('campaigns:registration.v2.visitAddress')} value={data.visitAddress} />
            {data.attachments?.length > 0 && (
              <div className="py-2 border-b border-gray-100">
                <span className="text-gray-600 text-sm block mb-1">{t('campaigns:registration.v2.referenceFiles')}</span>
                {data.attachments.map((f: any, i: number) => (
                  <a key={i} href={f.fileUrl} target="_blank" rel="noopener noreferrer" className="block text-blue-600 text-sm hover:underline">📎 {f.fileName}</a>
                ))}
              </div>
            )}
          </Section>

          {/* Section 3: Campaign Mission */}
          <Section title={`3. ${t('campaigns:registration.v2.sectionMission')}`}>
            <Field label={t('campaigns:registration.v2.numberOfRecruits')}>
              {data.numberOfRecruits ? `${data.numberOfRecruits} ${t('campaigns:registration.v2.personsUnit')}` : null}
            </Field>
            {data.contentChannel && (
              <div className="flex items-center gap-2 py-2 border-b border-gray-100">
                <span className="text-gray-600 text-sm">{t('campaigns:registration.v2.contentChannel')}</span>
                <div className="flex items-center gap-1 ml-auto">
                  <ChannelIcon channel={data.contentChannel} className="w-4 h-4" />
                  <span className="font-medium text-gray-900 text-sm">{t(`campaigns:registration.v2.channels.${data.contentChannel}`)}</span>
                </div>
              </div>
            )}
            {data.rewardAmount > 0 && (
              <div className="py-2 border-b border-gray-100">
                <div className="flex justify-between"><span className="text-gray-600 text-sm">{t('campaigns:registration.v2.reward')}</span><span className="font-medium text-sm">${data.rewardAmount.toLocaleString()}</span></div>
                <div className="flex justify-between mt-0.5"><span className="text-gray-500 text-xs">{t('campaigns:registration.v2.influencerSettlement')}</span><span className="text-blue-600 text-xs font-medium">${influencerAmount.toLocaleString()}</span></div>
              </div>
            )}

            {/* Publishing conditions */}
            {/* Publishing Conditions V2 — preview reads from new structure */}
            {(() => {
              const pc = data.publishingConditions;
              const igConds = pc?.instagram?.conditions || [];
              const igCollabId = pc?.instagram?.collaboratorId || data.instagramCollaboratorId;
              const blogConds = pc?.blog?.conditions || [];
              const blogLink = pc?.blog?.insertLink || data.blogInsertLink;
              const ytConds = pc?.youtube?.conditions || [];
              const ytLink = pc?.youtube?.insertLink || data.youtubeInsertLink;
              return (
                <>
                  {igConds.length > 0 && (
                    <div className="py-2 border-b border-gray-100">
                      <span className="text-gray-600 text-sm block mb-1">{t('campaigns:registration.v2.igConditions')}</span>
                      <div className="flex flex-wrap gap-1">{igConds.map((c: any, i: number) => (
                        <span key={i} className="px-2 py-0.5 bg-pink-100 text-pink-800 text-xs rounded-full">{t(`campaigns:registration.v2.igItems.${c.item}`)} × {c.quantity}</span>
                      ))}</div>
                      {igCollabId && <p className="mt-1 text-xs text-gray-500">공동작업자 ID: {igCollabId}</p>}
                    </div>
                  )}
                  {blogConds.length > 0 && (
                    <div className="py-2 border-b border-gray-100">
                      <span className="text-gray-600 text-sm block mb-1">{t('campaigns:registration.v2.blogConditions')}</span>
                      <div className="flex flex-wrap gap-1">{blogConds.map((c: any, i: number) => (
                        <span key={i} className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">{t(`campaigns:registration.v2.blogItems.${c.item}`)} × {c.quantity}</span>
                      ))}</div>
                      {blogLink && <p className="mt-1 text-xs text-gray-500">콘텐츠 내 삽입링크: {blogLink}</p>}
                    </div>
                  )}
                  {ytConds.length > 0 && (
                    <div className="py-2 border-b border-gray-100">
                      <span className="text-gray-600 text-sm block mb-1">{t('campaigns:registration.v2.ytConditions')}</span>
                      <div className="flex flex-wrap gap-1">{ytConds.map((c: any, i: number) => (
                        <span key={i} className="px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded-full">{t(`campaigns:registration.v2.ytItems.${c.item}`)} × {c.quantity}</span>
                      ))}</div>
                      {ytLink && <p className="mt-1 text-xs text-gray-500">콘텐츠 내 삽입링크: {ytLink}</p>}
                    </div>
                  )}
                </>
              );
            })()}

            {/* Schedule */}
            {(data.recruitmentPeriod?.startDate || data.announcementStartDate || data.visitStartDate) && (
              <div className="py-2 border-b border-gray-100 space-y-1">
                <span className="text-gray-600 text-sm block">{t('campaigns:registration.v2.campaignSchedule')}</span>
                {data.recruitmentPeriod?.startDate && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">{t('campaigns:registration.v2.recruitmentPeriod')}</span>
                    <span>{data.recruitmentPeriod.startDate}{data.recruitmentPeriod.alwaysRecruiting ? ` (${t('campaigns:registration.v2.alwaysRecruiting')})` : data.recruitmentPeriod.endDate ? ` ~ ${data.recruitmentPeriod.endDate}` : ''}</span>
                  </div>
                )}
                {data.announcementStartDate && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">{t('campaigns:registration.v2.announcementPeriod')}</span>
                    <span>{data.announcementStartDate}{data.announcementEndDate ? ` ~ ${data.announcementEndDate}` : ''}</span>
                  </div>
                )}
                {data.visitStartDate && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">{t('campaigns:registration.v2.visitPeriod')}</span>
                    <span>{data.visitStartDate}{data.visitEndDate ? ` ~ ${data.visitEndDate}` : ''}</span>
                  </div>
                )}
                {data.uploadDeadlineDays && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">{t('campaigns:registration.v2.uploadPeriod')}</span>
                    <span>{t('campaigns:registration.v2.afterVisit')} {data.uploadDeadlineDays}{t('campaigns:registration.v2.daysWithin')}</span>
                  </div>
                )}
                {data.discussSchedule && <span className="inline-block px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">{t('campaigns:registration.v2.discussOnProgress')}</span>}
              </div>
            )}

            {data.contentWritingRequirements && (
              <div className="py-2 border-b border-gray-100">
                <span className="text-gray-600 text-sm block mb-1">{t('campaigns:registration.v2.contentGuide')}</span>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{data.contentWritingRequirements}</p>
              </div>
            )}

            {data.secondarySubmissions?.length > 0 && (
              <div className="py-2 border-b border-gray-100">
                <span className="text-gray-600 text-sm block mb-1">{t('campaigns:registration.v2.secondarySubmissions')}</span>
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-50"><th className="px-2 py-1 text-left">{t('campaigns:registration.v2.itemSelect')}</th><th className="px-2 py-1 text-left">{t('campaigns:registration.v2.quantityVolume')}</th><th className="px-2 py-1 text-left">{t('campaigns:registration.v2.remarks')}</th></tr></thead>
                  <tbody>{data.secondarySubmissions.map((s: any, i: number) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="px-2 py-1">{t(`campaigns:registration.v2.secondaryTypes.${s.type}`)}</td>
                      <td className="px-2 py-1">{s.quantity} {s.type === 'video' ? t('campaigns:registration.v2.minutes') : t('campaigns:registration.v2.pieces')}</td>
                      <td className="px-2 py-1">{s.note || '-'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}

            <Field label={t('campaigns:registration.v2.submissionPath')} value={data.submissionPath} />
            {data.secondarySubmissionGuide && (
              <div className="py-2">
                <span className="text-gray-600 text-sm block mb-1">{t('campaigns:registration.v2.secondaryGuide')}</span>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{data.secondarySubmissionGuide}</p>
              </div>
            )}
          </Section>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end flex-shrink-0">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            {t('common:close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignPreviewModal;
